/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import { FC, useState, useEffect, useCallback } from 'react'
import { useParams, Outlet } from 'react-router'
import { useTranslation } from 'react-i18next'
import { useServiceContext } from '../../root'
import { WorkflowViewType } from './types'
import { assembleFiltersFromFormValues } from './utils'
import WorkflowsTable from './WorkflowsTable'
import WorkflowsFilter from './WorkflowsFilter'
import { RcmTableDataWrapper } from '../../components/rcm-table/RcmTableDataWrapper'
import RcmTableFilterTrigger from '../../components/rcm-table/RcmTableFilterTrigger'

/**
 * Workflows Screen Component
 *
 * Main workflows page that displays workflows based on the selected view type:
 * - active: Active workflows
 * - finished: Completed/archived workflows
 * - all: All workflows
 */
const Workflows: FC = () => {
  const { workflowsTab = 'active' } = useParams<{ workflowsTab: string }>()
  const { t } = useTranslation()
  const { service, api } = useServiceContext()

  const viewType = (
    workflowsTab === 'finished'
      ? WorkflowViewType.FINISHED
      : workflowsTab === 'all'
        ? WorkflowViewType.ALL
        : WorkflowViewType.ACTIVE
  ) as WorkflowViewType

  const isFilterShown = service.access.isAllowed('workflowsFilters')

  const [isInitialized, setIsInitialized] = useState(false)

  // Initialize
  useEffect(() => {
    if (!isInitialized) {
      setIsInitialized(true)
    }
  }, [isInitialized])

  // Reset when view type changes
  useEffect(() => {
    setIsInitialized(false)
  }, [viewType])

  // Create loadData function for RcmTableDataWrapper
  const loadData = useCallback(
    async (pagingParams: any, filter: any) => {
      // filter is passed from RcmTableDataWrapper (set by WorkflowsFilter via tableData.applyFilter)
      const activeFilter = filter || {}

      // Build base parameters with pagination and sorting
      const params: any = {
        pageSize: pagingParams.pageSize || 15,
        pageStart: pagingParams.pageStart || 0,
        sortBy: pagingParams.sortBy || 'START_DATE',
        sortDesc: pagingParams.sortDesc !== undefined ? pagingParams.sortDesc : true
      }

      // Add view type filter
      if (viewType === WorkflowViewType.ACTIVE) {
        params.workflowStatus = 'ACTIVE'
      } else if (viewType === WorkflowViewType.FINISHED) {
        params.workflowStatus = 'FINISHED'
      }
      // For ALL view, don't add active parameter

      // Add filter values if they exist
      if (activeFilter && isFilterShown) {
        // Workflow types (legacy field name: workflowType)
        if (activeFilter.workflowType && activeFilter.workflowType.length > 0) {
          params.workflowType = activeFilter.workflowType.map(String)
        }

        // Workflow phases (legacy field name: workflowPhase)
        if (activeFilter.workflowPhase && activeFilter.workflowPhase.length > 0) {
          params.workflowPhase = activeFilter.workflowPhase.map(String)
        }

        // Assigned to (legacy field name: assignedTo)
        if (activeFilter.assignedTo && activeFilter.assignedTo.length > 0) {
          params.assignedTo = activeFilter.assignedTo
        }

        // Workflow initiator (legacy field name: initiatorId)
        if (activeFilter.initiatorId && activeFilter.initiatorId.length > 0) {
          params.initiatorId = activeFilter.initiatorId
        }

        // Workflow ID (legacy field name: id)
        if (activeFilter.id && Array.isArray(activeFilter.id) && activeFilter.id.length > 0) {
          params.id = activeFilter.id.map((item: any) => (typeof item === 'object' ? item.value : item))
        }

        // Date range
        if (activeFilter.startFrom) {
          const d = new Date(activeFilter.startFrom)
          d.setHours(0, 0, 0, 0)
          params.startFrom = d.getTime()
        }
        if (activeFilter.startTo) {
          const d = new Date(activeFilter.startTo)
          d.setHours(23, 59, 59, 0)
          params.startTo = d.getTime()
        }

        // EOS fields
        if (activeFilter.study) {
          params.studyFolderId = activeFilter.study
        }
        if (activeFilter.site) {
          params.siteFolderId = activeFilter.site
        }

        // ETMF fsiIds + documentTypeTemplateId — use assembleFiltersFromFormValues for correct
        // cascade logic: product → study → environment → country → site, plus folderId
        const assembled = assembleFiltersFromFormValues(activeFilter, viewType)
        if (assembled.fsiIds && assembled.fsiIds.length > 0) {
          params.fsiIds = assembled.fsiIds
        }
        if (assembled.documentTypeTemplateId != null) {
          params.documentTypeTemplateId = assembled.documentTypeTemplateId
        }
      }

      // Call the workflows API service
      const response = await api.workflows.workflows.getList(params)

      return {
        elements: response.elements || [],
        totalElements: response.totalElements || 0
      }
    },
    [api, viewType, isFilterShown]
  )

  return (
    <div id="workflowsPage" className="mcc-content-frame theme-sandman">
      <RcmTableDataWrapper
        key={viewType} // Force remount when view type changes
        loadData={loadData}
        persistenceKey="workflows"
        defaultSortBy="START_DATE"
        defaultSortDesc={true}
        initialPage={1}
        doPreload={!isFilterShown} // Only preload if no filter - filter will trigger load when ready
      >
        <div className="sub-page-header">
          <div className="d-flex flex-wrap">
            <div className="align-left d-flex">
              <h1 id="da41b150-8483-4f9b-85e1-885c02cc83ba">{t('WORKFLOWS_TITLE', 'Workflows')}</h1>
            </div>
            {isFilterShown && (
              <div className="align-right align-bottom d-flex">
                <RcmTableFilterTrigger />
              </div>
            )}
          </div>
        </div>

        <div id="page-content">
          {isFilterShown && (
            <div id="workflows-filter">
              <WorkflowsFilter viewType={viewType} />
            </div>
          )}

          <WorkflowsTable viewType={viewType} filterShown={isFilterShown} />
        </div>
      </RcmTableDataWrapper>
      <Outlet />
    </div>
  )
}

export default Workflows
